import React, { useState, useEffect } from 'react'

const CookiePage: React.FC = () => {
  const [cookieSettings, setCookieSettings] = useState({
    necessary: true, // Always true, can't be changed
    functional: true,
    paypal: true
  })

  useEffect(() => {
    // Load current cookie settings
    const consent = localStorage.getItem('cookieConsent')
    if (consent) {
      if (consent === 'all') {
        setCookieSettings({
          necessary: true,
          functional: true,
          paypal: true
        })
      } else if (consent === 'necessary') {
        setCookieSettings({
          necessary: true,
          functional: false,
          paypal: false
        })
      } else {
        setCookieSettings({
          necessary: true,
          functional: false,
          paypal: false
        })
      }
    }
  }, [])

  const handleSave = () => {
    if (cookieSettings.functional && cookieSettings.paypal) {
      localStorage.setItem('cookieConsent', 'all')
    } else if (!cookieSettings.functional && !cookieSettings.paypal) {
      localStorage.setItem('cookieConsent', 'necessary')
    } else {
      // Custom settings
      localStorage.setItem('cookieConsent', JSON.stringify(cookieSettings))
    }
    
    // Show confirmation
    alert('Ihre Cookie-Einstellungen wurden gespeichert.')
  }

  const handleReset = () => {
    localStorage.removeItem('cookieConsent')
    setCookieSettings({
      necessary: true,
      functional: true,
      paypal: true
    })
    alert('Cookie-Einstellungen wurden zurückgesetzt. Beim nächsten Seitenaufruf erscheint der Cookie-Banner erneut.')
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <h1 className="text-3xl font-bold mb-8 text-center">Cookie-Einstellungen</h1>
        
        <div className="bg-white rounded-lg shadow-lg p-8 space-y-8">
          <div className="text-center">
            <p className="text-gray-700 mb-4">
              Hier können Sie Ihre Cookie-Einstellungen verwalten. Sie können Ihre Auswahl jederzeit ändern.
            </p>
          </div>

          <div className="space-y-6">
            {/* Necessary Cookies */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-xl font-semibold text-green-800">Notwendige Cookies</h2>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={true}
                    disabled
                    className="w-5 h-5 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                  />
                  <span className="ml-2 text-xs bg-green-600 text-white px-2 py-1 rounded">Immer aktiv</span>
                </div>
              </div>
              <p className="text-gray-700 mb-3">
                Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.
              </p>
              <div className="text-sm text-gray-600">
                <p><strong>Zweck:</strong> Session-Management, Sicherheit, Cookie-Einstellungen</p>
                <p><strong>Cookies:</strong> cookieConsent, sessionId, csrfToken</p>
                <p><strong>Speicherdauer:</strong> Session / 1 Jahr</p>
              </div>
            </div>

            {/* Functional Cookies */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-xl font-semibold text-blue-800">Funktionale Cookies</h2>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={cookieSettings.functional}
                    onChange={(e) => setCookieSettings({...cookieSettings, functional: e.target.checked})}
                    className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-600">
                    {cookieSettings.functional ? 'Aktiv' : 'Inaktiv'}
                  </span>
                </div>
              </div>
              <p className="text-gray-700 mb-3">
                Diese Cookies ermöglichen erweiterte Funktionen und verbessern Ihre Benutzererfahrung.
              </p>
              <div className="text-sm text-gray-600">
                <p><strong>Zweck:</strong> Warenkorb-Speicherung, Benutzereinstellungen, Sprachauswahl</p>
                <p><strong>Cookies:</strong> cart-data, user-preferences, theme-settings</p>
                <p><strong>Speicherdauer:</strong> 30 Tage</p>
              </div>
            </div>

            {/* PayPal Cookies */}
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-xl font-semibold text-indigo-800">PayPal-Cookies</h2>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={cookieSettings.paypal}
                    onChange={(e) => setCookieSettings({...cookieSettings, paypal: e.target.checked})}
                    className="w-5 h-5 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <span className="ml-2 text-sm text-gray-600">
                    {cookieSettings.paypal ? 'Aktiv' : 'Inaktiv'}
                  </span>
                </div>
              </div>
              <p className="text-gray-700 mb-3">
                Cookies von PayPal für sichere Zahlungsabwicklung und Betrugsschutz.
              </p>
              <div className="text-sm text-gray-600">
                <p><strong>Anbieter:</strong> PayPal Europe S.à r.l. et Cie, S.C.A.</p>
                <p><strong>Zweck:</strong> Zahlungsabwicklung, Sicherheit, Betrugsschutz</p>
                <p><strong>Cookies:</strong> PYPF, x-pp-*, tsrce, und weitere von PayPal verwaltete Cookies</p>
                <p><strong>Datenschutz:</strong> 
                  <a href="https://www.paypal.com/de/webapps/mpp/ua/privacy-full" className="text-indigo-600 hover:underline ml-1" target="_blank" rel="noopener noreferrer">
                    PayPal Datenschutzerklärung
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* Warning */}
          {(!cookieSettings.functional || !cookieSettings.paypal) && (
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
              <div className="flex">
                <svg className="w-5 h-5 text-yellow-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">Eingeschränkte Funktionalität</h3>
                  <p className="text-sm text-yellow-700 mt-1">
                    Das Deaktivieren bestimmter Cookies kann die Funktionalität der Website beeinträchtigen. 
                    PayPal-Zahlungen sind nur mit aktivierten PayPal-Cookies möglich.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t">
            <button
              onClick={handleSave}
              className="flex-1 bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
            >
              Einstellungen speichern
            </button>
            <button
              onClick={handleReset}
              className="flex-1 bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
            >
              Zurücksetzen
            </button>
          </div>

          <div className="text-center text-sm text-gray-600">
            <p>
              Sie können Ihre Cookie-Einstellungen jederzeit ändern, indem Sie diese Seite erneut besuchen oder 
              Ihre Browser-Einstellungen anpassen.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CookiePage